import React from 'react';
import {
  FinanceResponseData,
  OtherCharge,
} from '@upstart/harmony/lib/legos/financingTypes';
import { parseCurrencyTruncated } from '@upstart/harmony/lib/form-utils';
import { OtherChargeColumnType, OtherChargeFields } from '../ricFormTypes';
import Input, { PriceInput } from '../../../../components/PencilEditor/Input';
import { FormTable } from './FormItems';

const REQUIRED_OTHER_CHARGE_FIELDS: (keyof OtherCharge)[] = [
  'paidTo',
  'description',
];

// Helper to get display value for a charge (applies default description if empty)
const getDisplayCharge = (
  charge: OtherCharge,
  index: number,
  defaultDescriptions?: string[],
): OtherCharge => {
  const defaultDesc = defaultDescriptions?.[index];
  if (!charge?.description && defaultDesc) {
    return { ...charge, description: defaultDesc };
  }
  return charge;
};

export function ChargeTable(props: {
  columns: OtherChargeColumnType[];
  responseDataField: OtherChargeFields;
  responseData: FinanceResponseData;
  onChange: (data: Partial<FinanceResponseData>) => void;
  hideAmountInputs?: boolean;
  defaultDescriptions?: string[];
}) {
  const {
    columns,
    responseData,
    responseDataField,
    onChange,
    hideAmountInputs,
    defaultDescriptions,
  } = props;

  const charges: OtherCharge[] = responseData?.[responseDataField] ?? [];

  const updateChargeList = (
    newData: Partial<OtherCharge>,
    rowIndex: number,
  ) => {
    const newChargeList = [...charges];
    const currentCharge = newChargeList[rowIndex];
    const defaultDesc = defaultDescriptions?.[rowIndex];

    const updatedCharge = {
      ...currentCharge,
      ...newData,
    };

    // Apply default description if missing
    if (!updatedCharge.description && defaultDesc) {
      updatedCharge.description = defaultDesc;
    }

    newChargeList[rowIndex] = updatedCharge;
    return newChargeList;
  };

  const isDealerRebate = (fee: OtherCharge) =>
    fee?.description?.toLowerCase() === 'dealer rebate';

  return (
    <FormTable
      columns={columns}
      rows={charges.map((charge, rowIndex) => {
        const displayCharge = getDisplayCharge(
          charge,
          rowIndex,
          defaultDescriptions,
        );
        const isDescriptionReadOnly = isDealerRebate(displayCharge);

        return {
          descriptions: columns.map((column) => {
            const required =
              REQUIRED_OTHER_CHARGE_FIELDS.includes(column.fieldName) &&
              !!displayCharge?.amount;
            const isFieldReadOnly =
              column.fieldName === 'description' && isDescriptionReadOnly;

            return (
              <Input
                key={column.fieldName}
                required={required}
                value={displayCharge[column.fieldName]}
                placeholder="N/A"
                disabled={isFieldReadOnly}
                onChange={(val: string) =>
                  onChange({
                    [responseDataField]: updateChargeList(
                      { [column.fieldName]: val },
                      rowIndex,
                    ),
                  })
                }
              />
            );
          }),
          value: hideAmountInputs ? null : (
            <PriceInput
              value={displayCharge.amount}
              placeholder="N/A"
              disabled={false}
              onChange={(val) =>
                onChange({
                  [responseDataField]: updateChargeList(
                    { amount: parseCurrencyTruncated(val) },
                    rowIndex,
                  ),
                })
              }
            />
          ),
        };
      })}
    />
  );
}

export default ChargeTable;
